import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "../../../utils/axiosInstance";
import { MILLIS_TO_LOCALE } from "../../../utilities/helpers/formatUnix";
import {
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
} from "@mui/material";
import CreateConfigurationPopup from "./createConfigPopup";
import { PLATFORMS } from "../../../constants/configuration.const";
import DeleteModal from "../../UI/atoms/Popup/DeleteModal";
import { useNavigate } from "react-router";
import { enqueueSnackbar } from "notistack";

function createData(
    name: string,
    internalName: string,
    lastModified: string,
    status: string
) {
    return { name, internalName, lastModified, status };
}

const rows = [
    createData(
        "IOS Config",
        "ios-conf-123",
        new Date().toLocaleString(),
        "Published"
    ),
    createData(
        "IOS Config",
        "ios-conf-123",
        new Date().toLocaleString(),
        "Published"
    ),
    createData(
        "IOS Config",
        "ios-conf-123",
        new Date().toLocaleString(),
        "Published"
    ),
    createData(
        "IOS Config",
        "ios-conf-123",
        new Date().toLocaleString(),
        "Published"
    ),
    createData(
        "IOS Config",
        "ios-conf-123",
        new Date().toLocaleString(),
        "Published"
    ),
];

export const ConfigManagement = () => {
    // hook for handling list data
    const [configList, setConfigList] = useState([]);
    const [originalList, setOriginalList] = useState([]);
    const [openCreatePopup, setOpenCreatePopup] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<any>(null);
    const [platformMaster, setPlatformMaster] = useState([]);
    const [loader, setLoader] = useState(false);
    const navigate = useNavigate();

    const openDeleteModal = () => {
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    // get the data for populating on the list
    useEffect(() => {
        getConfigList();
        getMasterInformation();
    }, []);

    // make api call
    const getConfigList = async () => {
        try {
            setLoader(true);
            const listData = await axios.get(
                process.env.BASE_URL +
                    "api/v1/cms/config?offset=0&limit=&platform="
            );
            setConfigList(listData?.data?.data?.list);
            setOriginalList(listData?.data?.data?.list);
        } catch (error) {
            console.log("error fetching data from config", error);
        } finally {
            setLoader(false);
        }
    };

    // call API for getting the master information
    const getMasterInformation = async () => {
        try {
            const materInformation = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/locale/master`
            );
            if (materInformation.data?.success) {
                setPlatformMaster(materInformation.data?.data?.platform);
            }
        } catch (error) {
            console.log("Error in getting master information", error);
        }
    };

    // set create configuration popup open/close
    const handlePopupStatus = (status: boolean) => {
        setOpenCreatePopup(status);
    };

    // handle close callback
    const handleClosePopup = () => {
        handlePopupStatus(false);
    };

    // handleFilter
    const filterList = (event: any) => {
        const filterValue = event.target.value;
        // filter value is present
        if (filterValue) {
            const filterListByPlatform = originalList.filter(
                (val: any) => val?.platform === filterValue
            );
            setConfigList([...filterListByPlatform]);
        }
        // if empty, show all
        else {
            setConfigList([...originalList]);
        }
    };

    // on deleting a configuration
    const onDeleteConfiguration = (config: any) => {
        setSelectedConfig(config);
        openDeleteModal();
    };

    // Make delete API call for deleting the configuration
    const deleteConfiguration = async () => {
        try {
            if (selectedConfig) {
                setLoader(true);
                const deleteRecordResponse = await axios.delete(
                    process.env.BASE_URL +
                        "api/v1/cms/config/" +
                        selectedConfig.configId
                );
                enqueueSnackbar(deleteRecordResponse.data?.message, {
                    variant: deleteRecordResponse.data?.success
                        ? "success"
                        : "error",
                });
                getConfigList();
            } else {
                enqueueSnackbar("No record selected!", { variant: "info" });
            }
        } catch (error) {
            console.log("error fetching data from config", error);
        } finally {
            setSelectedConfig(null);
            setLoader(false);
            closeDeleteModal();
        }
    };

    // when user clicks on edit button. redirect the user to the edit screen
    const handleEditConfiguration = (id: any) => {
        navigate(`/localization/edit-config?configId=${id}`);
    };

    return (
        <div className="config-heading a-center">
            {/* Reusable delete popup */}
            <DeleteModal
                title="confirmation"
                isOpen={isDeleteModalOpen}
                handleClose={closeDeleteModal}
                handleConfirm={deleteConfiguration}
            >
                <p>
                    Are you sure to delete the configuration{" "}
                    <b>{selectedConfig?.profileName}</b>?
                </p>
            </DeleteModal>

            <div style={{ minWidth: "800px" }}>
                <div
                    className="header-content-list"
                    style={{ marginBottom: "10px", width: "100%" }}
                >
                    <div className="d-flex j-center a-center">
                        <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                            <InputLabel id="demo-simple-select-label">
                                Platform
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                className="filter-platform-select"
                                id="demo-simple-select"
                                label="platformFilter"
                                onChange={filterList}
                            >
                                <MenuItem value={""}>All</MenuItem>
                                {platformMaster.map((platform): any => {
                                    return (
                                        <MenuItem
                                            key={platform}
                                            value={platform}
                                        >
                                            {platform}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                        <Button
                            disabled
                            onClick={() => handlePopupStatus(true)}
                            variant="contained"
                        >
                            Create New Config
                        </Button>
                    </div>
                    <div>{loader && <CircularProgress size={30} />}</div>
                </div>
                {/* Configuration List Starts here */}
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <h3>Platform</h3>
                                </TableCell>
                                <TableCell>
                                    <h3 style={{ display: "inline" }}>Name</h3>{" "}
                                    (Canonical Name)
                                </TableCell>

                                <TableCell>
                                    <h3>Last Modified</h3>
                                </TableCell>
                                <TableCell>
                                    <h3>Actions</h3>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {configList &&
                                configList.map((row: any) => (
                                    <TableRow
                                        key={row.profileName}
                                        sx={{
                                            "&:last-child td, &:last-child th":
                                                {
                                                    border: 0,
                                                },
                                        }}
                                    >
                                        <TableCell>{row?.platform}</TableCell>
                                        <TableCell component="th" scope="row">
                                            <h5 style={{ display: "inline" }}>
                                                {row.profileName}
                                            </h5>{" "}
                                            ({row.internalName})
                                        </TableCell>

                                        <TableCell>
                                            {MILLIS_TO_LOCALE(row.lastModified)}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Edit Configuration">
                                                <EditIcon
                                                    className="action-icons"
                                                    onClick={() =>
                                                        handleEditConfiguration(
                                                            row.configId
                                                        )
                                                    }
                                                />
                                            </Tooltip>
                                            {/* <Tooltip title="Delete"><DeleteIcon className="action-icons" 
                                    style={{pointerEvents:'none'}} onClick={() => onDeleteConfiguration(row)} /></Tooltip> */}
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            {/* Configuration List ends here */}

            {/* Configuration Create Popup */}
            {openCreatePopup && (
                <CreateConfigurationPopup
                    platforms={platformMaster}
                    show={openCreatePopup}
                    handleCloseCB={handleClosePopup}
                />
            )}
        </div>
    );
};
